import React, { FC, ReactNode, useState } from 'react';

import classNames from 'classnames';

import { Popover } from '../Popover';
import { Text } from '../Text';

import styles from './menu.module.scss';
import { MenuItem } from './MenuItem';
import { MenuList } from './MenuList';

interface IMenuProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  children: ReactNode;
  /** Rich controls use ordinary Tab navigation, rather than menu arrow keys. */
  mode?: 'actions' | 'content';
  appearance?: 'default' | 'inset';
  placement?: 'bottom-start' | 'bottom-end';
  size?: 'fit' | 'small' | 'medium' | 'large';
  title?: ReactNode;
  description?: ReactNode;
  identityGap?: 'default' | 'none';
  itemAppearance?: 'action' | 'option';
  footer?: ReactNode;
  busy?: boolean;
  className?: string;
  'aria-label'?: string;
}

let nextMenuId = 0;

const MenuRoot: FC<IMenuProps> = ({
  anchorEl,
  onClose,
  children,
  mode = 'actions',
  appearance = 'default',
  placement = 'bottom-start',
  size = 'fit',
  title,
  description,
  identityGap = 'default',
  itemAppearance = 'action',
  footer,
  busy = false,
  className,
  'aria-label': ariaLabel,
}) => {
  const [id] = useState(() => {
    nextMenuId += 1;
    return `menu-${nextMenuId}`;
  });
  const titleId = title ? `${id}-title` : undefined;
  const descriptionId = description ? `${id}-description` : undefined;
  const label =
    ariaLabel ?? anchorEl?.getAttribute('aria-label') ?? anchorEl?.textContent;
  return (
    <Popover
      anchorEl={anchorEl}
      handleClose={onClose}
      placement={placement}
      fitToAnchor
      minWidth={180}
      busy={busy}
      id={id}
      role={mode === 'content' ? 'dialog' : undefined}
      className={appearance === 'inset' ? styles.insetPopover : undefined}
      aria-label={titleId ? undefined : label || undefined}
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
    >
      <div
        className={classNames(styles.surface, styles[size], className, {
          [styles.inset]: appearance === 'inset',
          [styles.options]: itemAppearance === 'option',
        })}
      >
        {(title || description) && (
          <div
            className={classNames(styles.header, {
              [styles.identityNoGap]: identityGap === 'none',
            })}
          >
            {title && (
              <div id={titleId}>
                <Text variant="bodySmall" className={styles.title}>
                  {title}
                </Text>
              </div>
            )}
            {description && (
              <div id={descriptionId}>
                <Text variant="bodySmall" color="var(--sk-text-subtle)">
                  {description}
                </Text>
              </div>
            )}
          </div>
        )}
        {mode === 'actions' ? (
          <MenuList
            enhanced
            aria-label={titleId ? undefined : label || undefined}
            aria-labelledby={titleId}
            aria-describedby={descriptionId}
          >
            {children}
          </MenuList>
        ) : (
          <div className={styles.content}>{children}</div>
        )}
        {footer && <div className={styles.footer}>{footer}</div>}
      </div>
    </Popover>
  );
};

/** Item and Items compose content; they do not create additional popup surfaces. */
const MenuItems: FC<React.ComponentProps<typeof MenuList>> = props => (
  <MenuList {...props} enhanced />
);
const Menu = Object.assign(MenuRoot, { Items: MenuItems, Item: MenuItem });

export { Menu };
